import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Field, FormikProvider, useFormik } from 'formik';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
    initialValues,
    validationSchema
} from '../Formik/createLessonFormikData';
import { createLab, deleteLab } from '../../../../API/Lab';
import modules from '../Components/Quill';
import {
    resetCurrentScreenForm,
    setCurrentScreenId,
    setCurrentScreenLoading,
    setCurrentScreenName,
    setCurrentScreenState
} from '../Slices/currentScreen';
import { store } from '../../../../store';
import QuizAssessmentDialog from './Components/QuizAssessmentDialog';
import LessonHeader from './Components/LessonHeader';
import VideoContentSection from './Components/VideoContentSection';
import PdfResourceSection from './Components/PdfResourceSection';
import AssessmentSection from './Components/AssessmentSection';
import VirtualLabSection from './Components/VirtualLabSection';
import DeleteConfirmDialog from './Components/DeleteConfirmDialog';
import VirtualLabDialog from './Components/VirtualLabDialog';
import {
    createLessons,
    createQuizAssessment,
    createResource,
    deleteLesson,
    deleteResources,
    editLesson,
    editResource,
    getCurrentLecture,
    getLectureExcercise,
    deleteLectureExcercise
} from '../../../../API/Lesson';

const Lesson = () => {
    const loader = useSelector((state) => state?.currentScreen?.isLoading);
    const [open, setOpen] = useState(false);
    const [allResourcesToCheck, setAllResourceToBeChecked] = useState([]);
    const [allResources, setAllResource] = useState([]);
    const [resourceToBeCreated, setresourceToBeCreated] = useState([]);
    const [resourceToBeDeletedId, setResourceToBeDeletedId] = useState([]);

    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [disableSubmitAssessment, setDisableSubmitAssessment] =
        useState(true);
    const [lectureId, setLectureId] = useState();
    const [draftAssessments, setDraftAssessments] = useState(null);
    const isSubmittingRef = useRef(false);

    const [showFileInput, setShowFileInput] = useState(true);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [video, setVideo] = useState('');
    const [assessments, setAssessments] = useState([]);
    const [lessonLabs, setLessonLabs] = useState([]);
    const [showLabForm, setShowLabForm] = useState(false);
    const [labFormData, setLabFormData] = useState({ labName: '', url: '' });

    const theme = useTheme();
    const handleClose = () => {
        setOpen(false);
    };
    const handleOpen = () => {
        setOpen(true);
    };
    const isEdit = useSelector((state) => state?.currentScreen?.editable);
    const shouldResetForm = useSelector(
        (state) => state?.currentScreen?.resetForm
    );
    const currentLessonId = useSelector((state) => state?.currentScreen?.id);
    const courseId = useSelector((state) => state?.currentCourse?.data?._id);
    const sectionId = useSelector(
        (state) => state?.currentCourse?.currentSectionId
    );

    // Look up the active manual (section) name for visual context
    const currentCourse = useSelector((state) => state?.currentCourse?.data);
    const activeSection = currentCourse?.sections?.find(
        (sec) => sec._id === sectionId
    );
    const activeSectionName = activeSection ? activeSection.title : '';

    const fetchAssessments = async (id) => {
        if (!id) return;
        const res = await getLectureExcercise(id);
        if (res && res.data) {
            setAssessments(res.data);
        }
    };
    useEffect(() => {
        setLectureId(currentLessonId);
    }, [currentLessonId]);

    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        questions: [
            {
                lectureId: '',
                question: '',
                options: {
                    option1: '',
                    option2: '',
                    option3: '',
                    option4: ''
                },
                answer: ''
            }
        ]
    });

    useEffect(() => {
        const hasEmptyAnswer = formData.questions.some(
            (q) => !q.answer || q.answer.trim() === ''
        );
        setDisableSubmitAssessment(hasEmptyAnswer);
    }, [formData.questions]);

    const handleCreateQuestionsClose = () => {
        setShowQuestionForm(false);
    };

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        const data = [...formData.questions];
        data[index].lectureId = lectureId;

        const trimmedName = name.replace(/^\s+/, '');
        const trimmedValue = value.replace(/^\s+/, '');

        if (trimmedName.slice(0, 6) === 'option') {
            data[index].options[trimmedName] = trimmedValue;
        } else {
            data[index][trimmedName] = trimmedValue;
        }

        setFormData({ ...formData, questions: data });
    };

    const addQuestion = () => {
        const newForm = {
            lectureId: '',
            question: '',
            options: {
                option1: '',
                option2: '',
                option3: '',
                option4: ''
            },
            answer: ''
        };
        setFormData((prev) => ({
            ...prev,
            questions: [...prev.questions, newForm]
        }));
    };

    const handleSubmitForm = async () => {
        let isEmpty = false;
        let isOptions = false;

        formData?.questions.forEach((obj) => {
            if (
                !obj.question ||
                obj.question.trim() === '' ||
                !obj.answer ||
                obj.answer.trim() === ''
            ) {
                isEmpty = true;
            }
            if (
                !obj.options.option1 ||
                obj.options.option1.trim() === '' ||
                !obj.options.option2 ||
                obj.options.option2.trim() === '' ||
                !obj.options.option3 ||
                obj.options.option3.trim() === '' ||
                !obj.options.option4 ||
                obj.options.option4.trim() === ''
            ) {
                isOptions = true;
            }
        });

        if (isEmpty || isOptions) {
            toast.error('Please fill all the fields');
        } else {
            if (!lectureId) {
                setDraftAssessments({ questions: [...formData.questions] });
                toast.success('Video assessment drafted successfully.');
                setShowQuestionForm(false);
                return;
            }

            await createQuizAssessment(lectureId, formData);
            toast.success('Video assessment has been created successfully.');
            fetchAssessments(lectureId);
            setFormData({
                questions: [
                    {
                        lectureId: '',
                        question: '',
                        options: {
                            option1: '',
                            option2: '',
                            option3: '',
                            option4: ''
                        },
                        answer: ''
                    }
                ]
            });
            setShowQuestionForm(false);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            if (shouldResetForm) {
                resetForm();
                dispatch(resetCurrentScreenForm(false));
            } else {
                if (isSubmittingRef.current) {
                    console.log('Submission blocked: already submitting');
                    return;
                }
                isSubmittingRef.current = true;
                try {
                    let currentVideo = video;
                    if (showLinkInput) {
                        currentVideo = '';
                        values.video = '';
                    } else if (showFileInput) {
                        values.externalLink = '';
                    }

                    if (isEdit) {
                        await editLesson(
                            currentLessonId,
                            currentVideo,
                            values,
                            dispatch
                        );
                        await Promise.all(
                            resourceToBeCreated.map(async (resource) => {
                                if (allResourcesToCheck.length > 0) {
                                    await createResource(
                                        currentLessonId,
                                        courseId,
                                        resource,
                                        values.resourceTitle
                                    );
                                } else if (
                                    resource.type === 'application/pdf'
                                ) {
                                    await createResource(
                                        currentLessonId,
                                        courseId,
                                        resource,
                                        values.resourceTitle
                                    );
                                } else {
                                    toast.error('Resource must be PDF only');
                                }
                            })
                        );

                        if (resourceToBeDeletedId.length > 0) {
                            await deleteResources(
                                resourceToBeDeletedId,
                                currentLessonId,
                                dispatch
                            );
                        }
                    } else if (showFileInput && !currentVideo) {
                        toast.error('Please upload video.');
                    } else if (showLinkInput && !values.externalLink) {
                        toast.error('Please enter external video link.');
                    } else {
                        const newLectureId = await createLessons(
                            values,
                            courseId,
                            sectionId,
                            currentVideo,
                            dispatch
                        );
                        setLectureId(newLectureId);

                        await Promise.all(
                            resourceToBeCreated.map(async (resource) => {
                                if (resource.type === 'application/pdf') {
                                    await createResource(
                                        newLectureId,
                                        courseId,
                                        resource,
                                        values.resourceTitle
                                    );
                                }
                            })
                        );

                        if (
                            draftAssessments &&
                            draftAssessments.questions &&
                            draftAssessments.questions.length > 0
                        ) {
                            const syncedQuestions =
                                draftAssessments.questions.map((q) => ({
                                    ...q,
                                    lectureId: newLectureId
                                }));
                            await createQuizAssessment(newLectureId, {
                                questions: syncedQuestions
                            });
                            setDraftAssessments(null);
                        }

                        const draftLabs = lessonLabs.filter(
                            (lab) =>
                                lab._id &&
                                lab._id.toString().startsWith('draft-')
                        );
                        if (draftLabs.length > 0) {
                            await Promise.all(
                                draftLabs.map((lab) =>
                                    createLab(
                                        newLectureId,
                                        { labName: lab.labName, url: lab.url },
                                        'lecture'
                                    )
                                )
                            );
                        }

                        await editLesson(
                            newLectureId,
                            currentVideo,
                            values,
                            dispatch
                        );
                    }
                } catch (error) {
                    console.error('Error during form submission:', error);
                } finally {
                    isSubmittingRef.current = false;
                }
            }
        }
    });

    const descPlainText = formik.values.description
        ? formik.values.description
              .replace(/<[^>]*>/g, '')
              .replace(/&nbsp;/g, ' ')
              .trim()
        : '';
    const descCharCount = descPlainText.length;
    const descWordCount = descPlainText
        ? descPlainText.split(/\s+/).filter(Boolean).length
        : 0;

    const handleDelete = async () => {
        await deleteLesson(
            currentLessonId,
            sectionId,
            dispatch,
            setResourceToBeDeletedId
        );
        handleClose();
        dispatch(setCurrentScreenId(sectionId));
    };

    const uploadVideo = (e) => {
        const vid = e.target.files[0];
        const data = vid.type;
        const result = data.substring(0, 5);

        if (result === 'video') {
            setVideo(vid);
            e.target.value = null;
        } else {
            toast.error('Please upload video only!');
        }
    };

    const uploadResource = (e) => {
        if (allResources.length >= 3) {
            toast.error('Cannot upload more than 3 resources');
        } else {
            const res = e.target.files[0];
            const data = res.type;
            if (data === 'application/pdf') {
                setAllResource([...allResources, res]);

                const existingResource = resourceToBeCreated?.find(
                    (resource) => resource.name === res.name
                );

                if (existingResource) {
                    toast.error('Same file');
                } else {
                    setresourceToBeCreated([...resourceToBeCreated, res]);
                }
                e.target.value = null;
            } else {
                toast.error('Please upload PDF resource.');
            }
        }
    };

    const handleDeleteResource = (id) => {
        const filtered = allResources.filter((resource) => resource._id !== id);
        setAllResource(filtered);
        setResourceToBeDeletedId([...resourceToBeDeletedId, id]);
    };

    const deleteVideo = (e) => {
        e.stopPropagation();
        setVideo('');
        formik.setFieldValue('video', '');
    };

    const handleDeleteAssessment = async (excerciseId, idx) => {
        if (!lectureId) {
            const updatedQuestions = [...draftAssessments.questions];
            updatedQuestions.splice(idx, 1);
            if (updatedQuestions.length === 0) {
                setDraftAssessments(null);
            } else {
                setDraftAssessments({ questions: updatedQuestions });
            }
            toast.success('Draft assessment question removed successfully');
            return;
        }

        const res = await deleteLectureExcercise(excerciseId);
        if (res && res.success) {
            toast.success('Assessment question removed successfully');
            fetchAssessments(lectureId);
        } else {
            toast.error('Failed to remove assessment question');
        }
    };

    const getCurrentLesson = useCallback(async () => {
        store.dispatch(setCurrentScreenLoading(true));
        await getCurrentLecture(currentLessonId).then((response) => {
            setAllResource(response.data.data.resource);
            setAllResourceToBeChecked(response.data.data.resource);
            setVideo(response.data.data.video);
            setLessonLabs(response.data.data.labs || []);
            formik.setValues(response.data.data, false);

            if (response.data.data.externalLink) {
                setShowLinkInput(true);
                setShowFileInput(false);
            } else {
                setShowFileInput(true);
                setShowLinkInput(false);
            }
        });
        await fetchAssessments(currentLessonId);
        store.dispatch(setCurrentScreenLoading(false));
    }, [currentLessonId]);

    const handleFormChange = (e) => {
        shouldResetForm && dispatch(resetCurrentScreenForm(false));
        const trimmedValue = e.target.value.replace(/^\s+/, '');
        formik.handleChange(e);
        formik.setFieldValue(e.target.name, trimmedValue);
    };

    useEffect(() => {
        if (currentLessonId) {
            getCurrentLesson();
        } else {
            setAssessments([]);
            setVideo('');
            setAllResource([]);
            setLessonLabs([]);
            setDraftAssessments(null);
        }
    }, [currentLessonId, getCurrentLesson]);

    const displayAssessments = lectureId
        ? assessments
        : draftAssessments?.questions || [];

    useEffect(() => {
        if (shouldResetForm) {
            setAllResource([]);
            setLessonLabs([]);
            formik.handleSubmit();
        }
    }, [shouldResetForm]);

    const deleteQuestion = (index) => {
        if (formData.questions.length > 1) {
            const updatedQuestions = [...formData.questions];
            updatedQuestions.splice(index, 1);
            setFormData((prevFormData) => ({
                ...prevFormData,
                questions: updatedQuestions
            }));
        }
    };

    const handleCreateLabSubmit = async () => {
        if (!labFormData.labName || !labFormData.url) {
            toast.error('Both Lab Name and URL are required.');
            return;
        }
        if (!lectureId) {
            const newLab = {
                labName: labFormData.labName,
                url: labFormData.url,
                _id: `draft-${Date.now()}`
            };
            setLessonLabs([...lessonLabs, newLab]);
            toast.success('Virtual Lab drafted successfully.');
            setShowLabForm(false);
            setLabFormData({ labName: '', url: '' });
            return;
        }
        try {
            await createLab(lectureId, labFormData, 'lecture');
            toast.success('Virtual Lab added to lesson!');
            setShowLabForm(false);
            setLabFormData({ labName: '', url: '' });
            getCurrentLesson();
        } catch (error) {
            toast.error('Failed to add Virtual Lab');
        }
    };

    const handleDeleteLab = async (id) => {
        if (id && id.toString().startsWith('draft-')) {
            const filtered = lessonLabs.filter((lab) => lab._id !== id);
            setLessonLabs(filtered);
            toast.success('Draft Virtual Lab removed successfully');
            return;
        }
        try {
            await deleteLab(id, lectureId, 'lecture');
            toast.success('Virtual Lab removed successfully');
            getCurrentLesson();
        } catch (error) {
            toast.error('Failed to remove Virtual Lab');
        }
    };

    return loader ? (
        <Box
            textAlign="center"
            height="60vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
        >
            <CircularProgress />
        </Box>
    ) : (
        <>
            <Box>
                <Card
                    elevation={3}
                    sx={{
                        borderRadius: '16px',
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        mb: 2
                    }}
                >
                    {/* Header */}
                    <LessonHeader
                        isEdit={isEdit}
                        activeSectionName={activeSectionName}
                        onDeleteClick={handleOpen}
                        theme={theme}
                    />

                    <CardContent sx={{ p: 4 }}>
                        <FormikProvider value={formik}>
                            <form onSubmit={formik.handleSubmit}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={3.5}
                                >
                                    {/* Section 1: Title & Description */}
                                    <Box>
                                        <TextField
                                            label="Lesson Name"
                                            fullWidth
                                            name="title"
                                            variant="outlined"
                                            value={formik.values.title}
                                            onChange={(e) =>
                                                handleFormChange(e)
                                            }
                                            error={
                                                formik.touched.title &&
                                                Boolean(formik.errors.title)
                                            }
                                            helperText={
                                                formik.touched.title &&
                                                formik.errors.title
                                            }
                                        />
                                    </Box>

                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary'
                                            }}
                                        >
                                            Lesson Description
                                        </Typography>
                                        <Box
                                            sx={{
                                                '& .ql-toolbar.ql-snow': {
                                                    borderColor:
                                                        formik.touched
                                                            .description &&
                                                        formik.errors
                                                            .description
                                                            ? `#CA0000 !important`
                                                            : `${theme.palette.divider} !important`
                                                },
                                                '& .ql-container': {
                                                    borderBottomLeftRadius:
                                                        '12px',
                                                    borderBottomRightRadius:
                                                        '12px',
                                                    minHeight: '200px',
                                                    fontSize: '1rem',
                                                    borderColor:
                                                        formik.touched
                                                            .description &&
                                                        formik.errors
                                                            .description
                                                            ? `#CA0000 !important`
                                                            : `${theme.palette.divider} !important`,
                                                    backgroundColor: '#fff'
                                                },
                                                '& .ql-toolbar': {
                                                    borderTopLeftRadius: '12px',
                                                    borderTopRightRadius:
                                                        '12px',
                                                    borderColor: `${theme.palette.divider} !important`,
                                                    backgroundColor:
                                                        theme.palette.grey[100]
                                                }
                                            }}
                                        >
                                            <Field name="description">
                                                {({ field }) => (
                                                    <ReactQuill
                                                        placeholder="Write what students will study in this lesson..."
                                                        theme="snow"
                                                        name="description"
                                                        value={field.value}
                                                        onChange={field.onChange(
                                                            field.name
                                                        )}
                                                        modules={modules}
                                                    />
                                                )}
                                            </Field>
                                        </Box>
                                    </Box>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mt: 0.5, px: 0.5 }}
                                    >
                                        <Box>
                                            {formik.touched.description &&
                                                Boolean(
                                                    formik.errors.description
                                                ) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                    >
                                                        {
                                                            formik.errors
                                                                .description
                                                        }
                                                    </Typography>
                                                )}
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            color={
                                                descWordCount > 1500
                                                    ? 'error'
                                                    : 'text.secondary'
                                            }
                                            sx={{ fontWeight: 600, ml: 'auto' }}
                                        >
                                            Characters: {descCharCount} | Words:{' '}
                                            {descWordCount} / 1500 allowed
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {/* Section 2: Video Content */}
                                    <VideoContentSection
                                        video={video}
                                        formik={formik}
                                        showFileInput={showFileInput}
                                        showLinkInput={showLinkInput}
                                        setShowFileInput={setShowFileInput}
                                        setShowLinkInput={setShowLinkInput}
                                        uploadVideo={uploadVideo}
                                        deleteVideo={deleteVideo}
                                        handleFormChange={handleFormChange}
                                        theme={theme}
                                    />

                                    <Divider />

                                    {/* Section 3: PDF Resources */}
                                    <PdfResourceSection
                                        formik={formik}
                                        allResources={allResources}
                                        uploadResource={uploadResource}
                                        handleDeleteResource={
                                            handleDeleteResource
                                        }
                                        handleFormChange={handleFormChange}
                                        theme={theme}
                                    />

                                    {/* Section 4: Assessments */}
                                    <Divider />
                                    <AssessmentSection
                                        onAddQuestion={() => {
                                            if (!lectureId) {
                                                if (
                                                    draftAssessments?.questions &&
                                                    draftAssessments.questions
                                                        .length > 0
                                                ) {
                                                    setFormData({
                                                        questions: JSON.parse(
                                                            JSON.stringify(
                                                                draftAssessments.questions
                                                            )
                                                        )
                                                    });
                                                } else {
                                                    setFormData({
                                                        questions: [
                                                            {
                                                                lectureId: '',
                                                                question: '',
                                                                options: {
                                                                    option1: '',
                                                                    option2: '',
                                                                    option3: '',
                                                                    option4: ''
                                                                },
                                                                answer: ''
                                                            }
                                                        ]
                                                    });
                                                }
                                            }
                                            setShowQuestionForm(true);
                                        }}
                                        displayAssessments={displayAssessments}
                                        handleDeleteAssessment={
                                            handleDeleteAssessment
                                        }
                                        theme={theme}
                                    />

                                    {/* Section 5: Virtual Labs */}

                                    <Divider sx={{ my: 3 }} />
                                    <VirtualLabSection
                                        lessonLabs={lessonLabs}
                                        lectureId={lectureId}
                                        handleDeleteLab={handleDeleteLab}
                                        onAddLab={() => setShowLabForm(true)}
                                        theme={theme}
                                    />

                                    {/* Bottom Action Panel */}
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mt={1}
                                    >
                                        <Box display="flex" gap={2}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                type="submit"
                                                disabled={formik.isSubmitting}
                                                sx={{
                                                    px: 4,
                                                    py: 1,
                                                    fontWeight: 'bold',
                                                    borderRadius: '8px',
                                                    textTransform: 'none'
                                                }}
                                            >
                                                {isEdit
                                                    ? 'Save Lecture'
                                                    : 'Submit Lecture'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </form>
                        </FormikProvider>
                    </CardContent>
                </Card>

                {/* Quiz Dialog Assessment */}
                <QuizAssessmentDialog
                    showQuestionForm={showQuestionForm}
                    handleCreateQuestionsClose={handleCreateQuestionsClose}
                    formData={formData}
                    handleChange={handleChange}
                    addQuestion={addQuestion}
                    handleSubmitForm={handleSubmitForm}
                    lectureId={lectureId}
                    setLectureId={setLectureId}
                    currentLessonId={currentLessonId}
                    theme={theme}
                    deleteQuestion={deleteQuestion}
                    setDisableSubmitAssessment={setDisableSubmitAssessment}
                    disableSubmitAssessment={disableSubmitAssessment}
                />

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmDialog
                    open={open}
                    onClose={handleClose}
                    onConfirm={handleDelete}
                    theme={theme}
                />

                {/* Virtual Lab Dialog */}
                <VirtualLabDialog
                    open={showLabForm}
                    onClose={() => setShowLabForm(false)}
                    labFormData={labFormData}
                    setLabFormData={setLabFormData}
                    onSubmit={handleCreateLabSubmit}
                    theme={theme}
                />
            </Box>
            <ToastContainer />
        </>
    );
};

export default Lesson;
